import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import {
  ErrorConfirmationDialogData,
  ErrorNotificationModalComponent,
} from "src/app/components/common/error-notification-modal/error-notification-modal.component";
import LocalizationConstants from "src/app/constants/localization-constants";
import { ServerError } from "src/app/models/ServerError";
import { AppInsightsService } from "src/app/services/app-insight.service";
import { ModalConfig, ModalService } from "src/app/services/modal.service";

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private static lastErrorMessage = "";

  constructor(
    private modalService: ModalService,
    private zone: NgZone,
    private appInsightsService: AppInsightsService,
  ) {}

  public async handleError(error: Error | HttpErrorResponse) {
    this.appInsightsService.logException(error);

    if (!this.shouldOpenErrorModal(error)) {
      return false;
    }

    let dialogConfig: ErrorConfirmationDialogData;

    // TODO: Find what is the correct type for error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promiseRejectionError = (error as any).rejection;
    const httpError = (promiseRejectionError as HttpErrorResponse) ?? error;

    // http error
    if (httpError instanceof HttpErrorResponse) {
      dialogConfig = await this.getServerErrorMessageAsync(httpError);
    } else if (promiseRejectionError) {
      const message =
        promiseRejectionError.message ?? LocalizationConstants.ERROR_CLIENT_SIDE_MESSAGE;

      dialogConfig = new ErrorConfirmationDialogData(
        LocalizationConstants.ERROR_CLIENT_SIDE_TITLE,
        [message],
      );
    }
    // js error
    else {
      dialogConfig = new ErrorConfirmationDialogData(
        LocalizationConstants.ERROR_CLIENT_SIDE_TITLE,
        [LocalizationConstants.ERROR_CLIENT_SIDE_MESSAGE],
      );
    }

    console.error(error);
    AppErrorHandler.lastErrorMessage = error.message;
    this.zone.run(() => {
      const modalConfig = new ModalConfig();
      modalConfig.width = "600px";
      modalConfig.data = dialogConfig;

      this.modalService.openModal(ErrorNotificationModalComponent, modalConfig);
    });
  }

  private shouldOpenErrorModal(error: Error): boolean {
    if (error.message === AppErrorHandler.lastErrorMessage) {
      return false;
    }

    return true;
  }

  private async getServerErrorMessageAsync(
    error: HttpErrorResponse,
  ): Promise<ErrorConfirmationDialogData> {
    let dialogData: ErrorConfirmationDialogData;

    if (error.status === HttpStatusCode.Unauthorized || error.status === HttpStatusCode.Forbidden) {
      dialogData = new ErrorConfirmationDialogData(
        LocalizationConstants.ERROR_NOT_AUTHORIZED_TITLE,
        [LocalizationConstants.ERROR_NOT_AUTHORIZED_MESSAGE],
      );
    } else if (error.status === HttpStatusCode.NotFound) {
      dialogData = new ErrorConfirmationDialogData(LocalizationConstants.ERROR_NOT_FOUND_TITLE, [
        LocalizationConstants.ERROR_NOT_FOUND_MESSAGE,
      ]);

      await this.setAdditionalErrorDataAsync(error, dialogData);
    }
    // all other client errors
    else if (
      error.status !== 0 && // errorStatus === 0 in case server is down
      error.status < HttpStatusCode.InternalServerError
    ) {
      dialogData = new ErrorConfirmationDialogData(LocalizationConstants.ERROR_BAD_REQUEST_TITLE, [
        LocalizationConstants.ERROR_BAD_REQUEST_MESSAGE,
      ]);

      await this.setAdditionalErrorDataAsync(error, dialogData);
    }
    // server error
    else {
      dialogData = new ErrorConfirmationDialogData(LocalizationConstants.ERROR_SERVER_ERROR_TITLE, [
        LocalizationConstants.ERROR_SERVER_ERROR_MESSAGE,
      ]);
    }

    // set traceId, if not already set by serverError model
    dialogData.requestId = dialogData.requestId ?? error?.error?.traceId;
    return dialogData;
  }

  /**
   * Sets the error details for clientSide error from provided HttpErrorResponse.
   */
  private async setAdditionalErrorDataAsync(
    error: HttpErrorResponse,
    dialogData: ErrorConfirmationDialogData,
  ): Promise<void> {
    dialogData.additionalDetails = [];
    for (const err in error.error?.errors) {
      dialogData.additionalDetails.push(error.error.errors[err].join(""));
    }

    // check if we have error detail
    if (error.error?.detail) {
      dialogData.additionalDetails.push(error.error?.detail);
    } else {
      // treat as promise rejection and try get the error details from
      // JSON error
      const serverError = await this.parseErrorAsync(error);
      if (serverError?.detail) {
        dialogData.additionalDetails.push(serverError.detail);
        dialogData.requestId = serverError.traceId;
      }
    }
  }

  private async parseErrorAsync(error: HttpErrorResponse): Promise<ServerError> {
    const plainMessage = error?.error;
    if (plainMessage) {
      return plainMessage;
    }

    const serverMessage = await error.error.text();
    return JSON.parse(serverMessage);
  }
}
